/**
 * HTML mail sanitisation.
 *
 * Email HTML is hostile input (docs/SECURITY.md §T1). Defence in depth:
 *
 * 1. DOMPurify strips active content — script, event handlers, `javascript:`
 *    URLs — while keeping benign presentational markup.
 * 2. The reader renders the result inside an iframe with `sandbox` lacking
 *    `allow-scripts` and `allow-same-origin`, so even a sanitiser bypass
 *    cannot execute script or reach page storage/cookies.
 *
 * Remote images are a tracking vector: they stay disabled by default and are
 * enabled per message by explicit user action.
 */
import DOMPurify from "dompurify";
import type { Config } from "dompurify";

/**
 * DOMPurify configuration for email bodies.
 */
const EMAIL_HTML_CONFIG: Config = {
  // Drop the whole element rather than its content: <script> children must
  // not leak in as text.
  FORBID_TAGS: ["script", "object", "embed", "iframe", "form", "base"],
  // Event-handler attributes are caught by the default hook set; style is
  // dropped to break CSS-based tracking/exfiltration in the first cut.
  FORBID_ATTR: ["style"],
};

/**
 * Config for the image pass: remove every `img` element outright.
 */
const NO_IMAGES_CONFIG: Config = {
  FORBID_TAGS: ["img"],
};

/**
 * Sanitises an email HTML body for rendering.
 *
 * @param html - Untrusted HTML from a message part.
 * @param options - Image handling for this render.
 * @param options.allowRemote - Keep remote image URLs (off by default; when
 * off, `img` elements are stripped entirely so nothing loads — no tracking
 * pixels, no remote fetches, no mixed-content surprises).
 * @returns HTML safe to embed in a sandboxed iframe.
 */
export function sanitizeEmailHtml(
  html: string,
  { allowRemote = false }: { allowRemote?: boolean } = {},
): string {
  const cleaned = DOMPurify.sanitize(html, EMAIL_HTML_CONFIG);
  if (allowRemote) {
    return cleaned;
  }
  // Second pass is cheap relative to the trust win.
  return DOMPurify.sanitize(cleaned, NO_IMAGES_CONFIG);
}
